/**
 * App 自然能力工具集
 * 包括网络状态处理、页面可见性处理、后台预加载、支付结果处理等
 */

import type { Router } from 'vue-router'
import { getWebSocketManager } from './websocket'
import { showFancyToast } from './toast'

// 后台超时阈值（5分钟）
const BACKGROUND_TIMEOUT = 5 * 60 * 1000

// 后台时间记录
let backgroundTime: number | null = null

/**
 * 网络恢复处理
 */
export const handleOnline = async (stores: { appStore: any; chatStore: any }) => {
  console.log('🌐 网络已恢复')
  stores.appStore.setNetworkStatus(true)

  // 🔧 网络恢复后，检查 WebSocket 连接状态
  const wsManager = getWebSocketManager()
  wsManager.checkAndFixConnection()

  // 🔧 网络恢复后延迟2秒补全断网期间丢失的消息
  // 注意：这里只处理浏览器级别网络恢复，WebSocket级别由WebSocket插件处理
  setTimeout(async () => {
    try {
      console.log('🔄 网络恢复，检查断网期间丢失的消息...')
      await stores.chatStore.recoverMissingAIReplies()
    } catch (error) {
      console.warn('网络恢复消息补全失败:', error)
    }
  }, 2000)
}

/**
 * 网络断开处理
 */
export const handleOffline = (stores: { appStore: any; chatStore: any }) => {
  console.warn('🌐 网络已断开，清理所有异常状态')
  stores.appStore.setNetworkStatus(false)

  // 🔧 修复：网络断开时，清理所有 loading 状态，防止永久 loading
  stores.chatStore.clearAllDigitalHumanStates({
    loading: false,
    typing: false
  })
}

/**
 * 页面可见性变化处理
 */
export const handleVisibilityChange = (stores: { appStore: any }) => {
  if (document.hidden) {
    // 🔧 切入后台：记录时间
    backgroundTime = Date.now()
    stores.appStore.setPageVisibility(false)
    console.log('📱 应用切入后台，记录时间:', new Date(backgroundTime).toLocaleString())
  } else {
    // 🔧 回到前台：检查时间差
    stores.appStore.setPageVisibility(true)

    if (backgroundTime) {
      const timeInBackground = Date.now() - backgroundTime
      const minutes = Math.floor(timeInBackground / 60000)
      console.log(`📱 应用回到前台，后台时长: ${minutes} 分钟`)

      // 如果超过5分钟，刷新页面
      if (timeInBackground > BACKGROUND_TIMEOUT) {
        console.log('🔄 后台时长超过5分钟，刷新页面...')
        window.location.reload()
      }

      // 重置后台时间
      backgroundTime = null
    }
  }
}

/**
 * 后台预加载处理 - 主要预加载 mine 页面
 */
export const handlePreload = async (
  event: Event,
  stores: {
    userStore: any
    vipStore: any
    storePageStore: any
  },
  router: Router
) => {
  const customEvent = event as CustomEvent
  const { reason } = customEvent.detail

  console.log(`🚀 开始后台预加载 mine 页面，原因: ${reason}`)

  try {
    // 1. 预加载 mine 页面数据
    await preloadMinePageData(stores)

    // 2. 预加载 mine 页面路由组件
    try {
      const resolved = router.resolve('/mine')
      console.log('✅ 预加载 mine 路由:', resolved)
    } catch (err) {
      console.warn('⚠️ 预加载 mine 路由失败:', err)
    }

    console.log('✅ mine 页面后台预加载完成')
  } catch (error) {
    console.error('❌ mine 页面后台预加载失败:', error)
  }
}

/**
 * 预加载 mine 页面数据
 */
const preloadMinePageData = async (stores: {
  userStore: any
  vipStore: any
  storePageStore: any
}) => {
  console.log('📦 开始预加载 mine 页面数据...')

  try {
    // 1. 预加载用户信息（mine 页面核心数据）
    if (stores.userStore.getStoredToken()) {
      if (!stores.userStore.userInfo?.userId) {
        await stores.userStore.getUserInfo()
        console.log('✅ 用户信息预加载完成')
      } else {
        console.log('✅ 用户信息已缓存')
      }

      // 2. 预加载 VIP 相关信息（通过 refreshVipPerks）
      try {
        if (!stores.vipStore.vipPerksInitialized) {
          await stores.vipStore.refreshVipPerks()
          console.log('✅ VIP 权益信息预加载完成')
        } else {
          console.log('✅ VIP 权益信息已缓存')
        }
      } catch (error) {
        console.warn('⚠️ VIP 权益信息预加载失败:', error)
      }

      // 3. 预加载充值项（mine 页面可能需要）
      try {
        if (stores.storePageStore.rechargeItems.length === 0) {
          await stores.storePageStore.fetchRechargeItems()
          console.log('✅ 充值项预加载完成')
        } else {
          console.log('✅ 充值项已缓存')
        }
      } catch (error) {
        console.warn('⚠️ 充值项预加载失败:', error)
      }
    } else {
      console.log('⚠️ 未登录，跳过 mine 页面数据预加载')
    }

    console.log('✅ mine 页面数据预加载完成')
  } catch (error) {
    console.warn('⚠️ mine 页面数据预加载失败:', error)
  }
}

/**
 * 支付结果处理
 */
export const handlePaymentResult = async (
  event: any,
  stores: {
    userStore: any
    vipStore: any
    storePageStore: any
  },
  icons: {
    successIcon: string
    errorIcon: string
  },
  logEventSafe: (eventName: string) => void
) => {
  try {
    const result = typeof event.detail === 'string' ? JSON.parse(event.detail) : event.detail

    if (result) {
      // 非续费订单处理
      if (!result.isRenew) {
        if (result.status === 'success') {
          // 支付成功
          showFancyToast('Thank you for your purchase', {
            subtitle: "Automatically Close in 5''",
            icon: icons.successIcon,
            duration: 1000
          })

          await stores.userStore.getUserInfo()

          // 设置 ultra 套餐
          const vipUltraPackage = JSON.parse(
            localStorage.getItem('vip_ultra_package_order') || '{}'
          )
          if (vipUltraPackage.packageName === 'ultra') {
            stores.vipStore.setVipUltraPackage(vipUltraPackage.packageIndex)
          }

          // 设置 plus 套餐
          const vipPlusPackage = JSON.parse(localStorage.getItem('vip_plus_package_order') || '{}')
          if (vipPlusPackage.packageName === 'plus') {
            stores.vipStore.setVipPlusPackage(vipPlusPackage.packageIndex)
          }

          // 设置挽留弹窗支付成功埋点
          if (localStorage.getItem('re_retention_popup') === 'true') {
            logEventSafe('re_retention_popup_click_buy_pay_success')
            localStorage.removeItem('re_retention_popup')
          }

          // 设置 $9.99 弹窗支付成功埋点
          if (localStorage.getItem('$9.99_popup_show') === 'true') {
            logEventSafe('$9.99_popup_click_pay_success')
            localStorage.removeItem('$9.99_popup_show')
          }

          // 设置用户信息
          await stores.userStore.setLoginTouristFunc()
        } else {
          // 支付失败
          // 设置挽留弹窗支付失败埋点
          if (localStorage.getItem('re_retention_popup') === 'true') {
            logEventSafe('re_retention_popup_click_buy_pay_failed')
            localStorage.removeItem('re_retention_popup')
          }
          // 设置 $9.99 弹窗支付成功埋点
          if (localStorage.getItem('$9.99_popup_show') === 'true') {
            logEventSafe('$9.99_popup_pay_success')
            localStorage.removeItem('$9.99_popup_click_pay_failed')
          }

          showFancyToast('Payment failed. Please try again', {
            subtitle: "Automatically Close in 5''",
            icon: icons.errorIcon,
            duration: 1000
          })
        }
      }

      // 刷新用户信息和充值项
      await stores.userStore.getUserInfo()
      await stores.storePageStore.fetchRechargeItems()
    }
  } catch (error) {
    console.error('❌ 支付结果处理错误:', error)
  }
}
